import React, { FC, useCallback, useEffect, useState, PropsWithChildren } from 'react';
import Button from '@mui/material/Button';
import { SxProps, Theme } from '@mui/material';
import { getFeedback } from '@sentry/nextjs';
import { isClient } from '../../constants';

interface SentryFeedbackButtonProps extends PropsWithChildren {
	sx?: SxProps<Theme>;
}

export const SentryFeedbackButton: FC<SentryFeedbackButtonProps> = ({ sx, children }) => {
	const { showSentryFeedbackButton, openSentryFeedbackModal } = useSentryFeedback();

	return showSentryFeedbackButton && (
		<Button
			id="sentry_feedback_custom_btn"
			sx={[
				{
					position: 'fixed',
					bottom: 20,
					right: 20,
					backgroundColor: '#ffffff',
					color: '#2b2233',
					border: '1.5px solid rgba(41, 35, 47, 0.13)',
					padding: '12px 16px',
					boxShadow: '0px 4px 24px 0px rgba(43, 34, 51, 0.12)',
					lineHeight: '25px',
					transition: 'opacity 0.1s ease-in-out',
					fontSize: '14px',
					fontWeight: 700,
					borderRadius: '12px',
					zIndex: 9999,

					'&:hover': {
						backgroundColor: '#f6f6f7',
						color: '#2b2233'
					},
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
			onClick={openSentryFeedbackModal}
		>
			{children ?? 'Report a Bug'}
		</Button>
	);
};

export const useSentryFeedback = () => {
	const [isShown, setIsShown] = useState(true);

	const getFeedbackManager = useCallback(() => getFeedback(), []);

	const openSentryFeedbackModal = useCallback(() => {
		const feedback = getFeedbackManager();

		if (!feedback) {
			return;
		}

		const widget = feedback.createWidget(); widget.appendToDom();

		const widgetId = 'sentry-feedback';
		widget.el.id = widgetId;
		widget.show();

		// Sentry's default dialog doesn't match well our default UI scaling
		const sentryDialogHolder = document.getElementById(widgetId);
		const style = document.createElement('style');
		style.innerHTML = '.dialog { width: 200vw; height: 200vh; }';
		sentryDialogHolder.shadowRoot.appendChild(style);
	}, [getFeedbackManager]);

	useEffect(() => {
		if (isClient && getFeedbackManager()) {
			setIsShown(true);
		}
	}, [getFeedbackManager]);

	return { showSentryFeedbackButton: isShown, openSentryFeedbackModal };
}